import { ref } from 'vue';
import type { Ref } from 'vue';

// Consent types
export type CookieConsentType = 'unknown' | 'necessary' | 'all';

// Cookie categories
export type CookieCategory = 'analytics' | 'marketing';

// Interface for composable
interface CookieConsentComposable {
    consent: Ref<CookieConsentType>;
    loadConsent: () => void;
    saveConsent: (value: 'necessary' | 'all') => void;
    hasConsentFor: (category: CookieCategory) => boolean;
    enableAnalytics: () => void;
    disableAnalytics: () => void;
}

// Global state (singleton)
const consent = ref<CookieConsentType>('unknown');

// Load consent from localStorage
const loadConsent = (): void => {
    try {
        const savedConsent = localStorage.getItem('cookieConsent');
        if (savedConsent === 'necessary' || savedConsent === 'all') {
            consent.value = savedConsent;
        } else {
            consent.value = 'unknown';
        }
        // console.log('Loaded consent from localStorage:', consent.value);
    } catch (error) {
        console.error('Error loading cookie consent from localStorage:', error);
        consent.value = 'unknown';
    }
};

// Save consent to localStorage
const saveConsent = (value: 'necessary' | 'all'): void => {
    try {
        localStorage.setItem('cookieConsent', value);
        consent.value = value;
        // console.log('Saved consent to localStorage:', value);
    } catch (error) {
        console.error('Error saving cookie consent to localStorage:', error);
    }
};

// Check if there is consent for a specific category
const hasConsentFor = (category: CookieCategory): boolean => {
    // If no consent given, no consent for anything
    if (consent.value === 'unknown') {
        return false;
    }

    // Necessary cookies are always allowed
    if (category === 'analytics' || category === 'marketing') {
        return consent.value === 'all';
    }

    // Default return false for unknown categories
    return false;
};

// Enable analytics
const enableAnalytics = (): void => {
    // Logic for enabling analytics scripts will be here
    // console.log('Enabling analytics scripts');
    // Example: window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
};

// Disable analytics
const disableAnalytics = (): void => {
    // Logic for disabling analytics scripts will be here
    // console.log('Disabling analytics scripts');
    // Example: window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
};

// Initialize consent loading on first module import
loadConsent();

// Create composable
export function useCookieConsent(): CookieConsentComposable {
    return {
        consent,
        loadConsent,
        saveConsent,
        hasConsentFor,
        enableAnalytics,
        disableAnalytics
    };
}